import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { auth } from './firebase';
import { signOut } from 'firebase/auth';
import { App } from './App';
import { SignIn } from './components/SignIn';
import { UpdateGate } from './update-check';
import { OrgProvider, useOrg, NoOrg } from './org';
import { initTheme } from './theme';
import { resolveHostBrand } from './host-brand';
import { AuthAction, isAuthAction } from './components/AuthAction';
import { MfaRequired } from './components/MfaRequired';
import { ClientPortal } from './portal';
import { BrandingProvider } from './branding';
import { enrolledFactors } from './lib/mfa';
import './styles.css';

/**
 * Which client this person represents, from their own token.
 *
 * `clientOf` is Record<orgId, clientId> and is a SEPARATE claim key from
 * `orgs` - see clientusers.ts. That separation is the whole security model:
 * "is this person staff" answers no by construction rather than by remembering
 * to exclude them, so reading this can never accidentally admit somebody to the
 * staff dashboard.
 *
 * Exactly one entry is expected, because the claim cannot express two clients
 * at one MSP. More than one org is legitimate - the same person can be a
 * customer of two MSPs running this product - and the first is taken until
 * there is a reason to offer a chooser.
 */
function clientClaim(claims: Record<string, unknown> | null): { orgId: string; clientIds: string[] } | null {
  const map = claims?.clientOf;
  if (!map || typeof map !== 'object') return null;

  /*
   * A LIST, OR A BARE STRING.
   *
   * The claim carries a list since 2026-08-10 so somebody can represent several
   * sites of one chain. A token minted before that holds a single string and is
   * not reissued until this user's claims are next touched - so both are read,
   * exactly as the Firestore rule does. Reading only the new shape would show a
   * blank portal to somebody whose access is perfectly valid.
   */
  for (const [orgId, value] of Object.entries(map as Record<string, unknown>)) {
    if (!orgId) continue;
    const ids = Array.isArray(value)
      ? value.map((x) => String(x ?? '').trim()).filter(Boolean)
      : [String(value ?? '').trim()].filter(Boolean);
    if (ids.length) return { orgId, clientIds: ids };
  }
  return null;
}

function Root() {
  const [user, setUser] = useState<User | null>(null);
  const [ready, setReady] = useState(false);
  const [client, setClient] = useState<{ orgId: string; clientIds: string[] } | null>(null);

  useEffect(
    () =>
      onAuthStateChanged(auth, (u) => {
        setUser(u);
        if (!u) { setClient(null); setReady(true); return; }
        /*
         * The claim is read BEFORE anything renders, because the answer decides
         * which product this person gets. Doing it later would flash the staff
         * shell - or worse, the "you belong to no organisation" screen - at a
         * customer who is exactly where they should be.
         */
        void u.getIdTokenResult()
          .then((r) => setClient(clientClaim(r.claims as Record<string, unknown>)))
          .catch(() => setClient(null))
          .finally(() => setReady(true));
      }),
    [],
  );

  /*
   * BEFORE ANYTHING ELSE, including the session check.
   *
   * Everyone arriving here is signed out, and some have no password yet - so
   * this cannot sit behind the sign-in form it exists to get people past. It
   * also must not wait on onAuthStateChanged: a stale session belonging to
   * somebody else on a shared machine would otherwise swallow the link
   * entirely and drop them on a dashboard that is not theirs.
   */
  if (isAuthAction()) return <AuthAction />;


  // Render nothing until Firebase has restored the session, otherwise every
  // reload flashes the sign-in form at an already-signed-in user.
  if (!ready) return <div className="boot">Loading…</div>;
  if (!user) return <SignIn />;

  /*
   * A CLIENT NEVER REACHES THE STAFF SHELL.
   *
   * Before OrgProvider, because myOrgs returns nothing for them - they hold no
   * `orgs` claim - and the honest-but-useless "you belong to no organisation"
   * screen is what every invited customer met until now. BrandingProvider still
   * wraps it so the portal wears their provider's logo rather than ours.
   */
  if (client) {
    return (
      <BrandingProvider orgId={client.orgId}>
        <ClientPortal orgId={client.orgId} clientIds={client.clientIds} />
      </BrandingProvider>
    );
  }

  // The org list has to load before anything can be read, because every query
  // is scoped to an organisation.
  return (
    <OrgProvider>
      <Scoped email={user.email} />
    </OrgProvider>
  );
}

function Scoped({ email }: { email: string | null }) {
  const { current, loading, error, reload } = useOrg();
  // Bumped when somebody finishes enrolling, to re-ask a question whose answer
  // lives in the Firebase SDK rather than in React state.
  const [enrolTick, setEnrolTick] = useState(0);

  if (loading) return <div className="boot">Loading…</div>;
  if (error) return <div className="boot">Could not load your organisations: {error}</div>;
  // Belonging to no org is a real state, not a failure - say so rather than
  // rendering an empty dashboard that looks broken.
  if (!current) {
    return <NoOrg email={email} onSignOut={() => void signOut(auth)} onRetry={() => void reload()} />;
  }

  /*
   * AFTER the org is known, because the requirement belongs to the org - and
   * BEFORE the dashboard, because a policy applied only to people who happen to
   * visit Settings is not a policy.
   *
   * Read straight off the live user rather than from state: enrolment happens
   * inside the Firebase SDK, and this asks the SDK.
   */
  if (current.requireMfa === true && enrolledFactors(auth.currentUser).length === 0) {
    return (
      <MfaRequired
        key={enrolTick}
        orgName={current.name}
        onEnrolled={() => setEnrolTick((n) => n + 1)}
      />
    );
  }

  return <App email={email} />;
}

initTheme();

/*
 * BRANDING BEFORE THE FIRST PAINT.
 *
 * Awaited rather than fired-and-forgotten so the tab title and the branding are
 * already right when the page appears - applying them a beat later produces a
 * visible flick from our mark to the tenant's, which reads as the wrong site
 * having loaded first. resolveHostBrand has its own timeout and swallows every
 * error, so this cannot delay or prevent the sign-in form.
 */
void resolveHostBrand().finally(() => {
  createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Root />
    {/* Sibling of the app on purpose: it must survive every view change, and it
        must show even when nobody is signed in - a stale build is exactly what
        would stop someone signing in at all. */}
      <UpdateGate />
    </React.StrictMode>
  );
});
